/**
 * Componente que renderiza la lista de usuarios en el radar.
 * Maneja los estados de carga, refresh y la visualización de usuarios en grilla.
 * Implementa CSS Grid responsive y optimizaciones de rendimiento.
 * 
 * @param {Object} props - Propiedades del componente
 * @param {boolean} props.loadingUsers - Si está cargando usuarios
 * @param {boolean} props.refreshing - Si está en modo refresh
 * @param {Array} props.usersList - Lista de usuarios
 * @param {string} props.feedbackMessage - Mensaje de estado
 * @param {Function} props.onRefresh - Función para refrescar
 * @param {Array} props.nearbyUsersIds - Lista de IDs de usuarios cercanos
 * @param {Object} props.location - Ubicación actual del usuario { lat, lng }
 * @param {Function} props.onOpenFriendProfile - Función para abrir el modal de perfil de amigo
 */

import React, { memo } from 'react';
import { FiRefreshCw } from 'react-icons/fi';
import UserCard from './UserCard';

const RadarGrid = memo(({
  usersList,
  loadingUsers,
  refreshing,
  feedbackMessage,
  onRefresh,
  nearbyUsersIds = [],
  location = null,
  onOpenFriendProfile,
}) => {
  // Move console.log to useEffect to avoid logging on every render
  React.useEffect(() => {
    console.log('[RadarGrid] Rendering with:', {
      loadingUsers,
      refreshing,
      usersListLength: usersList?.length,
      feedbackMessage,
    });
  }, [loadingUsers, refreshing, usersList?.length, feedbackMessage]);

  /**
   * Abre Google Maps con la ubicación actual del usuario
   * Usa las coordenadas lat/lng para mostrar un pin en el mapa
   */
  // const handleOpenGoogleMaps = useCallback(() => {
  //   if (!location || !location.lat || !location.lng) {
  //     console.warn('[RadarGrid] No hay ubicación disponible para abrir Google Maps');
  //     return;
  //   }

  //   // Construir URL de Google Maps con las coordenadas
  //   const googleMapsUrl = `https://www.google.com/maps?q=${location.lat},${location.lng}`;
    
  //   // Abrir en nueva pestaña
  //   window.open(googleMapsUrl, '_blank');
    
  //   console.log('[RadarGrid] Abriendo Google Maps con ubicación:', location);
  // }, [location]);

  /**
   * Renderiza el estado de carga con skeletons
   * Muestra 12 skeletons en grilla mientras carga
   */
  const renderLoadingState = () => (
    <div className="h-[calc(100dvh-9rem)] overflow-y-hidden m-1 grid grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-1">
      {Array.from({ length: 15 }).map((_, i) => (
        <div
          key={i}
          className="aspect-square bg-neutral-800 rounded-lg animate-pulse"
        />
      ))}
    </div>
  );

  /**
   * Renderiza el estado vacío cuando no hay usuarios
   * Muestra un mensaje amigable con instrucciones
   */
  const renderEmptyState = () => (
    <div className="flex flex-col items-center justify-center p-8 text-center">
      <p className="text-white text-lg mb-2">
        ¡Ups! Parece que no hay perfiles que coincidan con tus preferencias.
      </p>
      <p className="text-white/70 mb-4">
        Cámbialos para ver personas aquí.
      </p>
      <button
        onClick={onRefresh}
        className="mt-4 px-4 py-2 bg-primary rounded-lg flex items-center gap-2 hover:opacity-80 transition-opacity"
        style={{ backgroundColor: '#DA0037' }}
      >
        <FiRefreshCw className={refreshing ? 'animate-spin' : ''} size={20} />
        Actualizar
      </button>
    </div>
  );

  /**
   * Renderiza la grilla de usuarios
   * Usa CSS Grid responsive con 2-5 columnas según tamaño de pantalla
   */
  const renderUsersGrid = () => (
    <div className="p-1">
      {/* Grid responsive de usuarios */}
      <div className="grid grid-cols-3 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-5 gap-1">
        {usersList.map((user) => (
          <UserCard
            key={user.id}
            user={user}
            nearbyUsersIds={nearbyUsersIds}
            onOpenFriendProfile={onOpenFriendProfile}
          />
        ))}
      </div>
      <div className='h-10'></div>
    </div>
  );

  // Renderizado principal con manejo de errores
  try {
    return (
      <div className="flex-1">
        {loadingUsers ? (
          // Estado de carga inicial
          renderLoadingState()
        ) : usersList.length === 0 ? (
          // Estado vacío cuando no hay usuarios
          renderEmptyState()
        ) : (
          // Grid de usuarios encontrados
          renderUsersGrid()
        )}
      </div>
    );
  } catch (error) {
    console.error('[RadarGrid] Error rendering:', error);
    return (
      <div className="flex-1 flex items-center justify-center" style={{ backgroundColor: '#1E0312' }}>
        <p className="text-white">UsersList Error</p>
      </div>
    );
  }
});

export { RadarGrid };
